import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { ReactNode } from 'react'
import { Button } from '~/@/components/ui/button'
import { cn } from '~/@/lib/utils'
import { EnumOptionEditPanel } from './EnumOptionEditPanel'
import { EnumOptionFormValue } from './types'

interface EnumOptionEditPopoverProps {
  /** Null for the add-option trigger while closed, since a fresh option is created on each open */
  option: EnumOptionFormValue | null
  otherLabels: string[]
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSave: (option: EnumOptionFormValue) => void
  triggerContent: ReactNode
  triggerClassName?: string
  isMultiSelect: boolean
}

/** Opens the option edit panel in a popover below its trigger button */
export const EnumOptionEditPopover = ({
  option,
  otherLabels,
  isOpen,
  onOpenChange,
  onSave,
  triggerContent,
  triggerClassName,
  isMultiSelect,
}: EnumOptionEditPopoverProps) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context)])

  return (
    <>
      <Button
        ref={refs.setReference}
        className={cn('flex items-center gap-2', triggerClassName)}
        onClick={() => onOpenChange(!isOpen)}
        type="button"
        variant="ghost"
        {...getReferenceProps()}
      >
        {triggerContent}
      </Button>

      {isOpen && option && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className="z-[2000]"
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <EnumOptionEditPanel
                isMultiSelect={isMultiSelect}
                onCancel={() => onOpenChange(false)}
                onSave={(updated) => {
                  onSave(updated)
                  onOpenChange(false)
                }}
                option={option}
                otherLabels={otherLabels}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
